/**
 * UK (+44) / Ireland (+353) phone helpers for web: loose length checks and
 * stripping duplicate country codes so we never submit +44+44…
 */

const CC_UK = '44';
const CC_IE = '353';

function dialDigits(dialCode) {
  return String(dialCode || '').replace(/^\+/, '');
}

/**
 * @param {string} dialCode e.g. '+44', '+353', '+86'
 * @param {string} rawInput user input (may contain spaces)
 * @returns {{ nationalDigits: string, strippedCc: boolean }}
 */
export function normalizeNationalDigits(dialCode, rawInput) {
  let d = String(rawInput || '').replace(/\D/g, '');
  const cc = dialDigits(dialCode);
  if (cc !== CC_UK && cc !== CC_IE) {
    return { nationalDigits: d, strippedCc: false };
  }
  if (d.startsWith('0')) {
    d = d.slice(1);
  }
  let strippedCc = false;
  for (let i = 0; i < 6; i += 1) {
    if (d.startsWith(cc) && d.length > cc.length) {
      d = d.slice(cc.length);
      strippedCc = true;
    } else {
      break;
    }
  }
  return { nationalDigits: d, strippedCc };
}

/**
 * Loose length rules (national part only, after normalize).
 */
export function validateUkIeNationalLength(dialCode, nationalDigits) {
  const cc = dialDigits(dialCode);
  if (cc !== CC_UK && cc !== CC_IE) {
    return { ok: true };
  }
  const len = nationalDigits.length;
  if (!len) {
    return { ok: false, message: 'Please enter your phone number.' };
  }
  if (cc === CC_UK) {
    if (len < 9 || len > 12) {
      return {
        ok: false,
        message:
          'For +44 (UK), enter 9–12 digits after the country code (e.g. 7… or 07…). Do not type 44 again in the number box.',
      };
    }
  }
  if (cc === CC_IE) {
    if (len < 8 || len > 11) {
      return {
        ok: false,
        message:
          'For +353 (Ireland), enter 8–11 digits after the country code. Do not type 353 again in the number box.',
      };
    }
  }
  return { ok: true };
}

/**
 * @returns {{ ok: boolean, nationalDigits: string, message?: string, strippedCc: boolean }}
 */
export function normalizeAndValidatePhoneForSubmit(dialCode, rawInput) {
  const { nationalDigits, strippedCc } = normalizeNationalDigits(dialCode, rawInput);
  const lenRes = validateUkIeNationalLength(dialCode, nationalDigits);
  if (!lenRes.ok) {
    return { ok: false, nationalDigits, message: lenRes.message, strippedCc };
  }
  return { ok: true, nationalDigits, strippedCc };
}

/**
 * +44 / +353: length rules above. Other dial codes: at least 6 digits.
 */
export function validatePhoneWithIntlRules(dialCode, rawInput) {
  const r = normalizeAndValidatePhoneForSubmit(dialCode, rawInput);
  if (!r.ok) {
    return r;
  }
  const cc = dialDigits(dialCode);
  if (cc !== CC_UK && cc !== CC_IE && r.nationalDigits.length < 6) {
    return {
      ok: false,
      nationalDigits: r.nationalDigits,
      message: 'Please enter a valid phone number.',
      strippedCc: r.strippedCc,
    };
  }
  return r;
}

/**
 * Whether the normalized national part is longer than allowed (+44 / +353 only).
 */
export function isNationalLengthOverMax(dialCode, rawInput) {
  const cc = dialDigits(dialCode);
  if (cc !== CC_UK && cc !== CC_IE) return false;
  const maxNat = cc === CC_UK ? 12 : 11;
  const { nationalDigits } = normalizeNationalDigits(dialCode, rawInput);
  return nationalDigits.length > maxNat;
}
